import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import './PermitTracker.css';
import { API_BASE } from '../config/apiConfig';

const PermitTracker = () => {
  const { submissionId } = useParams();
  const navigate = useNavigate();
  const [permit, setPermit] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchPermitStatus = useCallback(async () => {
    try {
      const response = await axios.get(`${API_BASE}/status/${submissionId}`);
      setPermit(response.data);
      setLoading(false);
      setError(null);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to fetch permit status');
      setLoading(false);
    }
  }, [submissionId]);

  useEffect(() => {
    fetchPermitStatus();
    // Poll for status updates every 30 seconds
    const interval = setInterval(fetchPermitStatus, 30000);
    return () => clearInterval(interval);
  }, [fetchPermitStatus]);

  const handleCloneApplication = async () => {
    try {
      const response = await axios.get(`${API_BASE}/re-apply/${submissionId}`);
      const permitType = response.data.data?.permitType || 'building';
      // Navigate to appropriate form with pre-filled data
      const formPath = permitType === 'row' ? '/submit/row' : '/submit/building';
      navigate(formPath, { 
        state: { 
          prefillData: response.data.data,
          originalSubmissionId: submissionId,
          rejectionReason: response.data.rejectionReason
        } 
      });
    } catch (err) {
      alert('Failed to load application data: ' + (err.response?.data?.error || err.message));
    }
  };

  if (loading) {
    return <div className="tracker-container">Loading...</div>;
  }

  if (error) {
    return <div className="tracker-container error">{error}</div>;
  }

  const stages = [
    { key: 'submitted', label: 'Submitted', icon: '' },
    { key: 'under_review', label: 'Under Review', icon: '' },
    { key: 'department_coordination', label: 'Department Coordination', icon: '' },
    { key: 'decision', label: 'Decision', icon: '' },
    { key: 'issued', label: 'Issued', icon: '' }
  ];

  const currentStageIndex = stages.findIndex(s => s.key === permit.status);
  const isRejected = permit.isRejected || permit.status === 'rejected';

  return (
    <div className="tracker-container">
      <div className="tracker-card">
        <h2>Permit Application Status</h2>
        <div className="submission-info">
          <p><strong>Submission ID:</strong> {permit.submissionId}</p>
          {permit.tylerId && <p><strong>Tyler ID:</strong> {permit.tylerId}</p>}
          <p><strong>Submitted:</strong> {new Date(permit.timestamp).toLocaleString()}</p>
        </div>

        {/* Visual Timeline */}
        <div className={`timeline ${isRejected ? 'rejected' : ''}`}>
          {stages.map((stage, index) => {
            const isActive = index <= currentStageIndex;
            const isCurrent = index === currentStageIndex;
            const isRejectionPoint = isRejected && stage.key === 'decision';

            return (
              <div key={stage.key} className="timeline-item">
                <div className={`timeline-step ${isActive ? 'active' : ''} ${isRejectionPoint ? 'rejected' : ''} ${isCurrent ? 'current' : ''}`}>
                  <div className="step-icon">{stage.icon}</div>
                  <div className="step-label">{stage.label}</div>
                  {isRejectionPoint && (
                    <div className="rejection-badge">DENIED</div>
                  )}
                </div>
                {index < stages.length - 1 && (
                  <div className={`timeline-line ${isActive && !isRejectionPoint ? 'active' : ''} ${isRejectionPoint ? 'rejected' : ''}`}></div>
                )}
              </div>
            );
          })}
        </div>

        {/* Rejection Message */}
        {isRejected && permit.rejectionReason && (
          <div className="rejection-message">
            <h3>Application Rejected</h3>
            <p><strong>Reason:</strong> {permit.rejectionReason}</p>
            <p className="rejection-note">
              Due to legal regulations, rejected permit applications cannot be edited. 
              You must submit a new application.
            </p>
          </div>
        )}

        {/* Clone Feature for Rejected Applications */}
        {isRejected && (
          <div className="clone-section">
            <button 
              className="clone-button"
              onClick={handleCloneApplication}
            >
              Start New Application based on this data
            </button>
            <p className="clone-note">
              This will pre-fill all your previous information so you can quickly create a new application with the necessary corrections.
            </p>
          </div>
        )}

        {/* AI Screening Status */}
        {permit.aiStatus && (
          <div className={`ai-status ai-${permit.aiStatus}`}>
            <h3>
              {permit.aiStatus === 'green' && 'AI Screening: PASSED'}
              {permit.aiStatus === 'yellow' && 'AI Screening: NEEDS REVIEW'}
              {permit.aiStatus === 'red' && 'AI Screening: FAILED'}
              {permit.aiStatus === 'error' && 'AI Screening: ERROR'}
            </h3>
            {permit.aiReasoning && (
              <div className="ai-reasoning">
                <strong>AI Reasoning:</strong>
                <p>{permit.aiReasoning}</p>
              </div>
            )}
            {permit.aiFindings && permit.aiFindings.length > 0 && (
              <div className="ai-findings">
                <strong>Findings:</strong>
                <ul>
                  {permit.aiFindings.map((finding, idx) => (
                    <li key={idx} className={`finding-${finding.severity}`}>
                      <strong>{finding.category}:</strong> {finding.description}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}

        {/* Status Summary */}
        <div className="status-summary">
          <h3>Current Status</h3>
          <p className={`status-badge ${permit.status} ${isRejected ? 'rejected' : ''}`}>
            {permit.status.replace(/_/g, ' ').toUpperCase()}
          </p>
        </div>
      </div>
    </div>
  );
};

export default PermitTracker;
